import React, { memo } from 'react';
import EkonomiskAnalysSimpleUrval from './SimpleUrval/EkonomiskAnalysSimpleUrval';
import AdvancedUrvalButton from '../../../../../../../../../../../Components/Urval/Components/AdvancedUrvalButton/AdvancedUrvalButton';
import {
  EKONOMISK_ANALYS_URVAL_ROUTINE_ID,
  EK_KONTO_URVAL_COLLECTION_NAME,
} from '../../../../../../../../../../../Components/Urval/Consts/UrvalConsts';
import { IKontodelar } from '../../../../../../Queries/EkonomiskAnalysKontodelarQuery/EkonomiskAnalysKontodelarQuery';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { IKontoValues } from '../../../../../../Queries/EkonomiskAnalysKontobegreppQuery/EkonomiskAnalysKontobegreppQuery';
import {
  IUIUrval,
  RutinUrvalType,
} from '../../../../../../../../../../../Types/UrvalTypes';
import { ISelectedAnalysCodePrivilege } from '../../../../../../EkonomiskAnalysPage';
import RutinUrvalListQuery from '../../../../../../../../../../../Components/Urval/Components/SavedUrval/Queries/RutinUrvalListQuery/RutinUrvalListQuery';
import { queryNodeUrval } from '../../../../../../../../../../../Components/Urval/Components/SavedUrval/Queries/NodeUrvalQuery/queryNodeUrval';
import { convertUrval } from '../../../../../../../../../../../Components/Urval/Components/SavedUrval/Utils/convertSavedUrval';
import Logger from '../../../../../../../../../../../Utils/Logger';
import {
  ModelContextConsumer,
  AvenyCollectionMap,
} from '../../../../../../../../../../../Model/ModelContext/ModelContext';
import { ApolloClient, ApolloConsumer } from '@apollo/client';
import { RutinUrvalListQueryVariables } from '../../../../../../../../../../../Components/Urval/Components/SavedUrval/Queries/RutinUrvalListQuery/_QueryTypes/RutinUrvalListQuery';
import MenuButtonItem from '../../../../../../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButtonItem';
import EDListItemText from '../../../../../../../../../../../BaseComponents/List/Components/EDListItemText';
import { getVisibilityLabel } from '../../../../../../../../../../../Components/Urval/Components/SavedUrval/Utils/SavedUrvalUtils';
import EDMenuButton from '../../../../../../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButton';

interface IOuterProps {
  urval: IUIUrval | null;
  kontodelarValues: IKontodelar;
  kontobegreppValues: IKontoValues[];
  setUrval: (urval: IUIUrval) => void;
  codePrivilege: ISelectedAnalysCodePrivilege | null;
  codeSystemId?: string;
  advancedButtonTooltip?: string;
  kodbehorighetsUrval?: IUIUrval;
  autoFocus?: boolean;
  updateReport: (updateOrig?: boolean) => void;
}

interface IProps extends IOuterProps {
  apolloClient: ApolloClient<any>;
  collectionMap: AvenyCollectionMap;
  rutinUrvalList?: RutinUrvalType[] | null;
  rutinUrvalVariables?: RutinUrvalListQueryVariables;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      advancedButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginRight: theme.spacing(-1),
        paddingTop: theme.spacing(1),
      },
    } as const)
);

export const EkonomiskAnalysUrval: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = ({
  urval,
  kontodelarValues,
  kontobegreppValues,
  setUrval,
  codeSystemId,
  advancedButtonTooltip,
  kodbehorighetsUrval,
  autoFocus,
  apolloClient,
  collectionMap,
  rutinUrvalList,
  rutinUrvalVariables,
  updateReport,
}) => {
  const { classes } = useStyles();
  const handleUrvalClick = (id: string) => {
    queryNodeUrval(apolloClient, id)
      .then(queryUrval => {
        const savedUrval = convertUrval(
          queryUrval,
          EK_KONTO_URVAL_COLLECTION_NAME,
          collectionMap
        );
        setUrval(savedUrval);
      })
      .catch(error => {
        Logger.error(error);
      });
  };

  const urvalList =
    rutinUrvalList == null || rutinUrvalVariables == null ? [] : rutinUrvalList;

  return (
    <div>
      <EDMenuButton
        iconColor="primary"
        icon={'folder_open'}
        buttonLabel="Öppna sparat urval"
        primary
        tooltip="Öppna sparat urval"
      >
        {urvalList.map(x => (
          <MenuButtonItem
            key={x.id}
            onClick={() => handleUrvalClick(x.id)}
            category={getVisibilityLabel(x.synlighet).PO}
          >
            <EDListItemText primaryTypographyProps={{ variant: 'body2' }}>
              {x.namn}
            </EDListItemText>
          </MenuButtonItem>
        ))}
      </EDMenuButton>

      <EkonomiskAnalysSimpleUrval
        urval={urval}
        kontodelar={kontodelarValues}
        kontobegrepp={kontobegreppValues}
        setUrval={setUrval}
        kodbehorighetsUrval={kodbehorighetsUrval}
        autoFocus={autoFocus}
        updateReport={updateReport}
      />

      <div className={classes.advancedButtonContainer}>
        <AdvancedUrvalButton
          showIcon
          collectionName={EK_KONTO_URVAL_COLLECTION_NAME}
          rutinUrvalList={rutinUrvalList}
          rutinUrvalVariables={rutinUrvalVariables}
          urval={urval}
          setUrval={setUrval}
          kodplanUrval={true}
          routineID={EKONOMISK_ANALYS_URVAL_ROUTINE_ID}
          codeSystemID={codeSystemId}
          kodbehorighetsUrval={kodbehorighetsUrval}
          buttonTooltip={advancedButtonTooltip}
        />
      </div>
    </div>
  );
};

/**
 * En vy med alla inställningar för analysens urval
 */
const EnhancedEkonomiskAnalysUrval: React.FunctionComponent<
  React.PropsWithChildren<IOuterProps>
> = props => (
  <ModelContextConsumer>
    {({ avenyCollectionMap }) => (
      <ApolloConsumer>
        {client => (
          <RutinUrvalListQuery
            codeSystemID={props.codeSystemId}
            collectionName={EK_KONTO_URVAL_COLLECTION_NAME}
            routineID={EKONOMISK_ANALYS_URVAL_ROUTINE_ID}
            kodplanUrval={true}
          >
            {({ rutinUrvalList, variables }) => (
              <EkonomiskAnalysUrval
                collectionMap={avenyCollectionMap}
                apolloClient={client}
                rutinUrvalList={rutinUrvalList}
                rutinUrvalVariables={variables}
                {...props}
              />
            )}
          </RutinUrvalListQuery>
        )}
      </ApolloConsumer>
    )}
  </ModelContextConsumer>
);

export default memo(EnhancedEkonomiskAnalysUrval);
